import useAPI, { useAPIMutation } from "@/services/api/hook";
import {
  TermsVersionChangesResponse,
  TermsVersionDetailResponse,
  TermsVersionListResponse
} from "./types";

interface EnabledOption {
  enabled?: boolean;
}

// The contract's saved terms versions, newest first. Shared by the timeline and
// the pending-amendment badge, which read the same list rather than two calls —
// the query key is identical, so TanStack serves both from one request.
export const useTermsVersions = (
  contractId: string | undefined,
  options: EnabledOption = {}
) => {
  const result = useAPI(
    "contracts/terms/versions",
    { pathParams: { contractId: contractId ?? "" } },
    { enabled: options.enabled !== false && !!contractId }
  );

  return {
    ...result,
    data: result.data as TermsVersionListResponse | undefined
  };
};

// What one version changed against the version before it. Fetched on demand:
// the timeline expands one entry at a time, so this stays off the initial load.
export const useTermsVersionChanges = (
  contractId: string | undefined,
  versionId: string | undefined,
  options: EnabledOption = {}
) => {
  const result = useAPI(
    "contracts/terms/versionChanges",
    {
      pathParams: {
        contractId: contractId ?? "",
        versionId: versionId ?? ""
      }
    },
    {
      enabled: options.enabled !== false && !!contractId && !!versionId,
      // What one saved version changed is frozen history — it cannot change
      // after the fact. Without this the entry keeps an active observer for as
      // long as it stays mounted (the panel is no longer unmounted on collapse,
      // so the transition has something to animate), and the app-wide 5-minute
      // staleTime would refetch an immutable diff for every collapsed panel on
      // the next window focus.
      staleTime: Infinity
    }
  );

  return {
    ...result,
    data: result.data as TermsVersionChangesResponse | undefined
  };
};

// One version's full terms and its frozen figures, for the read-only View terms.
export const useTermsVersionDetail = (
  contractId: string | undefined,
  versionId: string | undefined,
  options: EnabledOption = {}
) => {
  const result = useAPI(
    "contracts/terms/versionDetail",
    {
      pathParams: {
        contractId: contractId ?? "",
        versionId: versionId ?? ""
      }
    },
    {
      enabled: options.enabled !== false && !!contractId && !!versionId
    }
  );

  return {
    ...result,
    data: result.data as TermsVersionDetailResponse | undefined
  };
};

interface WithdrawVersionMutationData {
  pathParams: {
    contractId: string;
    versionId: string;
  };
}

// Backs the product-facing Discard action; `useDiscardAmendment` orchestrates it.
// Path params come from each call, so none are seeded here.
export const useWithdrawTermsVersion = () =>
  useAPIMutation<WithdrawVersionMutationData, { contractId: string }>(
    "contracts/terms/versionWithdraw"
  );
